import {
  ANALYTICS_CLIENT_ID_STORAGE_KEY,
  getPublicWebAnalyticsConsent,
  isPublicWebAnalyticsAllowed,
  onPublicWebAnalyticsConsentChanged,
} from "./analytics-consent";
import { runWithAnalyticsConsent } from "./analytics-consent-policy.mjs";
import {
  initializeGoogleTag,
  revokeGoogleTagConsent,
  sanitizedPageLocation,
  sanitizedPageReferrer,
  sendGoogleTagEvent,
  setGoogleTagPageLocation,
  setGoogleTagUserProperties,
  type GoogleTagUserProperties,
} from "./google-tag";
import { loadFirstRunState } from "@/shared/onboarding/first-run";
import { hasStoredPreferences, loadPreferences } from "@/shared/preferences/store";
import {
  resolveSelectedLocationAnalyticsContext,
  type SelectedLocationAnalyticsContext,
} from "./selected-location-policy.mjs";
import { isSignedIn } from "@/shared/auth/session";

const ANALYTICS_ENDPOINT = "/api/analytics/event";
const ANALYTICS_SESSION_STORAGE_KEY = "tb_public_web_analytics_session_v2";
const ANALYTICS_SCHEMA_VERSION = 2;
const SESSION_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const MAX_ENGAGEMENT_TIME_MS = 60 * 60 * 1000;
// Four reserved fields are added for Measurement Protocol parity.
const MAX_PARAM_KEYS = 20;

const ANDROID_INSTALL_EVENTS = [
  "public_web_android_install_viewed",
  "public_web_android_install_clicked",
  "public_web_android_install_dismissed",
] as const;
const ANDROID_INSTALL_PLACEMENTS = new Set(["hero", "dock", "platform", "footer"]);

const EVENT_NAMES = [
  ...ANDROID_INSTALL_EVENTS,
  "public_web_route_viewed",
  "public_web_brief_started",
  "public_web_brief_preparing",
  "public_web_brief_completed",
  "public_web_brief_section_flagged",
  "public_web_brief_style_selected",
  "public_web_audio_play_started",
  "public_web_audio_queued",
  "public_web_audio_download_clicked",
  "public_web_preferences_opened",
  "public_web_save_toggled",
  "public_web_task_created",
  "public_web_premium_cta_tapped",
  "public_web_perf_navigation",
  "public_web_selected_country_updated",
  "subscription_trial_started",
  "page_view",
  "login",
  "sign_up",
  "share",
  "begin_checkout",
] as const;

export type PublicWebAnalyticsEvent = (typeof EVENT_NAMES)[number];
export type AnalyticsParamValue = string | number | boolean | null | undefined;
export type AnalyticsParams = Record<string, AnalyticsParamValue>;

type AnalyticsSessionContext = {
  sessionId: number;
  engagementTimeMsec: number;
};

type StoredAnalyticsSession = {
  sessionId: number;
  lastActivityEpochMs: number;
};

type AnalyticsConsentSnapshot = {
  analyticsStorage: "granted";
  adStorage: "denied";
  adUserData: "denied";
  adPersonalization: "denied";
  globalPrivacyControl: boolean;
};

type AnalyticsEnvelope = {
  schemaVersion: typeof ANALYTICS_SCHEMA_VERSION;
  eventId: string;
  occurredAtEpochMs: number;
  clientId: string;
  sessionId: number;
  engagementTimeMsec: number;
  eventName: PublicWebAnalyticsEvent;
  params: Record<string, string | number>;
  userProperties: GoogleTagUserProperties;
  consent: AnalyticsConsentSnapshot;
  clientForwardedToGa4: boolean;
};

type SelectedLocationAnalyticsSnapshot = {
  context: SelectedLocationAnalyticsContext;
  resolved: boolean;
};

const EVENT_NAME_SET = new Set<string>(EVENT_NAMES);
const ANDROID_INSTALL_EVENT_SET = new Set<string>(ANDROID_INSTALL_EVENTS);
const SAFE_PARAM_KEY = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const BLOCKED_PARAM_KEY = /(email|phone|token|otp|password|secret|address|name|url|link|payload)/i;

let auditEndpointDisabled = false;
let fallbackClientId = "";
let fallbackSession: StoredAnalyticsSession | null = null;
let previousPageLocation = "";
let lastAuthenticated = false;
let lastReportedSelectedLocationFingerprint = "";

function randomId(): string {
  const cryptoValue = globalThis.crypto;
  if (cryptoValue && typeof cryptoValue.randomUUID === "function") {
    return cryptoValue.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoValue && typeof cryptoValue.getRandomValues === "function") {
    cryptoValue.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
}

function getAnalyticsClientId(): string {
  if (typeof window === "undefined") return fallbackClientId || "server-render";
  try {
    const existing = window.localStorage.getItem(ANALYTICS_CLIENT_ID_STORAGE_KEY);
    if (existing) return existing.slice(0, 64);
    const created = `web2-${randomId()}`.slice(0, 64);
    window.localStorage.setItem(ANALYTICS_CLIENT_ID_STORAGE_KEY, created);
    return created;
  } catch {
    if (!fallbackClientId) fallbackClientId = `web2-${randomId()}`.slice(0, 64);
    return fallbackClientId;
  }
}

function cleanParamValue(value: AnalyticsParamValue): string | number | null {
  if (value == null) return null;
  if (typeof value === "string") return value.trim().slice(0, 100);
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  return null;
}

function cleanParams(
  params: AnalyticsParams = {},
  eventName?: PublicWebAnalyticsEvent,
): Record<string, string | number> {
  // Install attribution is a fixed UI placement, never browser/device details,
  // campaign parameters, referrers, or the Play Store destination URL.
  if (eventName && ANDROID_INSTALL_EVENT_SET.has(eventName)) {
    const placement = params.placement;
    return typeof placement === "string" && ANDROID_INSTALL_PLACEMENTS.has(placement)
      ? { placement }
      : {};
  }
  const out: Record<string, string | number> = {};
  let count = 0;
  for (const [key, value] of Object.entries(params)) {
    if (count >= MAX_PARAM_KEYS) break;
    if (!SAFE_PARAM_KEY.test(key) || key.length > 40 || BLOCKED_PARAM_KEY.test(key)) continue;
    const cleaned = cleanParamValue(value);
    if (cleaned == null || cleaned === "") continue;
    out[key] = cleaned;
    count += 1;
  }
  return out;
}

function newSessionId(nowEpochMs: number): number {
  return Math.max(1, Math.floor(nowEpochMs / 1000));
}

function readAnalyticsSession(): StoredAnalyticsSession | null {
  if (typeof window === "undefined") return fallbackSession;
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY) || "null",
    ) as Partial<StoredAnalyticsSession> | null;
    if (
      parsed &&
      Number.isSafeInteger(parsed.sessionId) &&
      Number(parsed.sessionId) > 0 &&
      Number.isFinite(parsed.lastActivityEpochMs) &&
      Number(parsed.lastActivityEpochMs) > 0
    ) {
      return {
        sessionId: Number(parsed.sessionId),
        lastActivityEpochMs: Number(parsed.lastActivityEpochMs),
      };
    }
  } catch {
    return fallbackSession;
  }
  return null;
}

function writeAnalyticsSession(session: StoredAnalyticsSession): void {
  fallbackSession = session;
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // The process-local fallback preserves a coherent session without storage.
  }
}

function nextAnalyticsSessionContext(nowEpochMs: number): AnalyticsSessionContext {
  const stored = readAnalyticsSession();
  const previousActivity = Number(stored?.lastActivityEpochMs || 0);
  const isCurrent =
    stored &&
    nowEpochMs >= previousActivity &&
    nowEpochMs - previousActivity <= SESSION_INACTIVITY_TIMEOUT_MS;
  const sessionId = isCurrent ? stored.sessionId : newSessionId(nowEpochMs);
  const engagementTimeMsec = isCurrent
    ? Math.max(1, Math.min(MAX_ENGAGEMENT_TIME_MS, nowEpochMs - previousActivity))
    : 1;
  writeAnalyticsSession({ sessionId, lastActivityEpochMs: nowEpochMs });
  return { sessionId, engagementTimeMsec };
}

function consentSnapshot(): AnalyticsConsentSnapshot {
  const decision = getPublicWebAnalyticsConsent();
  return {
    analyticsStorage: "granted",
    adStorage: "denied",
    adUserData: "denied",
    adPersonalization: "denied",
    globalPrivacyControl: decision.globalPrivacyControl,
  };
}

function selectedLocationAnalyticsSnapshot(): SelectedLocationAnalyticsSnapshot {
  try {
    const authenticatedAccount = isSignedIn();
    const firstRun = loadFirstRunState();
    const storedPreferences = hasStoredPreferences();
    const preferences = storedPreferences ? loadPreferences() : null;
    return {
      context: resolveSelectedLocationAnalyticsContext({
        authenticatedAccount,
        firstRunCompleted: firstRun.completed,
        hasStoredPreferences: storedPreferences,
        firstRunCountry: firstRun.country,
        preferencesCountry: preferences?.country,
      }),
      resolved: !authenticatedAccount || storedPreferences,
    };
  } catch {
    return {
      context: resolveSelectedLocationAnalyticsContext(),
      resolved: false,
    };
  }
}

function selectedLocationFingerprint(userProperties: GoogleTagUserProperties): string {
  return userProperties.selected_country;
}

function synchronizeSelectedLocationUserProperties(): void {
  if (!isPublicWebAnalyticsAllowed()) return;
  const snapshot = selectedLocationAnalyticsSnapshot();
  setGoogleTagUserProperties(snapshot.context.userProperties);
  if (
    snapshot.resolved &&
    selectedLocationFingerprint(snapshot.context.userProperties) !==
      lastReportedSelectedLocationFingerprint
  ) {
    const fingerprint = selectedLocationFingerprint(snapshot.context.userProperties);
    if (trackPublicWebEvent("public_web_selected_country_updated")) {
      lastReportedSelectedLocationFingerprint = fingerprint;
    }
  }
}

function postAnalytics(payload: AnalyticsEnvelope): boolean {
  if (!isPublicWebAnalyticsAllowed() || auditEndpointDisabled) return false;
  const body = JSON.stringify(payload);
  // Product analytics is installation-scoped, even while the person is signed in.
  // Keep this request credential-free so the server outbox can never bind an event
  // to account claims through a bearer header or ambient cookies.
  void fetch(ANALYTICS_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "omit",
    keepalive: true,
    body,
  })
    .then((res) => {
      if (res.status === 503) auditEndpointDisabled = true;
    })
    .catch(() => null);
  return true;
}

export function initializePublicWebAnalytics(): Promise<boolean> {
  return initializeGoogleTag();
}

export function trackPublicWebEvent(
  eventName: PublicWebAnalyticsEvent,
  params: AnalyticsParams = {},
): boolean {
  if (typeof window === "undefined" || !EVENT_NAME_SET.has(eventName)) return false;
  let deliveryAttempted = false;
  runWithAnalyticsConsent(getPublicWebAnalyticsConsent(), () => {
    const occurredAtEpochMs = Date.now();
    const eventId = randomId();
    const session = nextAnalyticsSessionContext(occurredAtEpochMs);
    const cleanedParams = cleanParams(params, eventName);
    const sharedParams = {
      ...cleanedParams,
      surface: "public_web_v2",
    };
    const selectedLocationSnapshot = selectedLocationAnalyticsSnapshot();
    const selectedLocation = selectedLocationSnapshot.context;
    const userProperties = selectedLocation.userProperties;
    const clientForwardedToGa4 = sendGoogleTagEvent(
      eventName,
      {
        ...sharedParams,
        event_id: eventId,
        delivery_transport: "web_gtag",
      },
      userProperties,
    );
    const firstPartyDeliveryAttempted = postAnalytics({
      clientId: getAnalyticsClientId(),
      schemaVersion: ANALYTICS_SCHEMA_VERSION,
      eventId,
      occurredAtEpochMs,
      sessionId: session.sessionId,
      engagementTimeMsec: session.engagementTimeMsec,
      eventName,
      params: {
        ...sharedParams,
        event_id: eventId,
        session_id: session.sessionId,
        engagement_time_msec: session.engagementTimeMsec,
      },
      userProperties,
      consent: consentSnapshot(),
      clientForwardedToGa4,
    });
    deliveryAttempted = clientForwardedToGa4 || firstPartyDeliveryAttempted;
    if (selectedLocationSnapshot.resolved && clientForwardedToGa4) {
      lastReportedSelectedLocationFingerprint = selectedLocationFingerprint(userProperties);
    }
  });
  return deliveryAttempted;
}

export function trackRouteView(route: string): void {
  trackPublicWebEvent("public_web_route_viewed", { route });
}

export function trackPageView(route: string): void {
  const pageLocation = sanitizedPageLocation(route);
  const pageReferrer = previousPageLocation || sanitizedPageReferrer();
  setGoogleTagPageLocation(pageLocation);
  trackPublicWebEvent("page_view", {
    route,
    page_location: pageLocation,
    page_title: route,
    page_referrer: pageReferrer || undefined,
  });
  previousPageLocation = pageLocation;
}

export function trackPerformanceNavigation(params: AnalyticsParams): void {
  trackPublicWebEvent("public_web_perf_navigation", params);
}

function clearPersistedAnalyticsSession(): void {
  fallbackSession = null;
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ANALYTICS_SESSION_STORAGE_KEY);
  } catch {
    // The consent gate still prevents reuse of the process-local session.
  }
}

function resetAnalyticsIdentityWhenConsentIsOff(): void {
  if (isPublicWebAnalyticsAllowed()) return;
  fallbackClientId = "";
  previousPageLocation = "";
  lastReportedSelectedLocationFingerprint = "";
  clearPersistedAnalyticsSession();
  revokeGoogleTagConsent();
  try {
    window.localStorage.removeItem(ANALYTICS_CLIENT_ID_STORAGE_KEY);
  } catch {
    // The consent gate still blocks collection when storage cannot be changed.
  }
}

function handleAnalyticsStateChanged(): void {
  const wasAuthenticated = lastAuthenticated;
  const nextAuthenticated = isSignedIn();
  lastAuthenticated = nextAuthenticated;

  if (!isPublicWebAnalyticsAllowed()) {
    resetAnalyticsIdentityWhenConsentIsOff();
    return;
  }

  lastReportedSelectedLocationFingerprint = "";
  void initializeGoogleTag().then(() => {
    if (!isPublicWebAnalyticsAllowed()) return;
    synchronizeSelectedLocationUserProperties();
    if (!wasAuthenticated && nextAuthenticated) {
      trackPublicWebEvent("login", { method: "account" });
    }
  });
}

if (typeof window !== "undefined") {
  lastAuthenticated = isSignedIn();
  resetAnalyticsIdentityWhenConsentIsOff();
  onPublicWebAnalyticsConsentChanged(handleAnalyticsStateChanged);
  if (isPublicWebAnalyticsAllowed()) {
    void initializeGoogleTag().then(() => {
      synchronizeSelectedLocationUserProperties();
    });
  }
}
